"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.QRCode = exports.QRCodePreview = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const styled_components_1 = require("styled-components");
const ui_common_components_1 = require("@desygner/ui-common-components");
const ui_common_qrcode_1 = require("@desygner/ui-common-qrcode");
const ui_common_translation_1 = require("@desygner/ui-common-translation");
const components_1 = require("../../components");
const QRCode_style_1 = require("./QRCode.style");
const components_2 = require("./components");
const PremiumToggle_1 = require("./components/PremiumToggle/PremiumToggle");
const QRCodeLogo_1 = require("./components/QRCodeLogo/QRCodeLogo");
const qrcode_utils_1 = require("./qrcode.utils");
const useQRCodeUploader_1 = require("./useQRCodeUploader");
function QRCodePreview({ type, value, qrCodeOption, waterMarkUrl, }) {
    const defaultUrl = (0, react_1.useMemo)(() => {
        var _a;
        return (_a = (0, ui_common_qrcode_1.createQRCode)('Desygner', qrCodeOption)) !== null && _a !== void 0 ? _a : '';
    }, [qrCodeOption]);
    const svgPreview = (0, react_1.useMemo)(() => {
        if (type === 'url') {
            if ((0, qrcode_utils_1.validateUrl)(value)) {
                return (0, ui_common_qrcode_1.createQRCode)(value, qrCodeOption);
            }
        }
        else {
            if ((0, qrcode_utils_1.validateVCard)(value)) {
                return (0, ui_common_qrcode_1.createVCardQRCode)(value, qrCodeOption);
            }
        }
        return defaultUrl;
    }, [type, value, qrCodeOption, defaultUrl]);
    return ((0, jsx_runtime_1.jsx)(QRCode_style_1.StyledQRPreview, { waterMarkUrl: waterMarkUrl, children: svgPreview && ((0, jsx_runtime_1.jsx)(QRCode_style_1.StyledQRImage, { isDefault: svgPreview === defaultUrl, dangerouslySetInnerHTML: { __html: svgPreview } })) }));
}
exports.QRCodePreview = QRCodePreview;
function QRCode({ backgroundColor, qrCodeColor, borderColor, margin, minMargin, maxMargin, onGenerateQRCodeEvent, showProUserPaywall, isProUser = false, waterMarkUrl, companionUrl, bucketName, objectKey, variant = 'default', }) {
    const theme = (0, styled_components_1.useTheme)();
    const { t } = (0, ui_common_translation_1.useTranslation)();
    const [isUploadLogo, setIsUploadLogo] = (0, react_1.useState)(false);
    const [isCustomisedOpen, setIsCustomisedOpen] = (0, react_1.useState)(false);
    const [qrCodeOption, setQRCodeOption] = (0, react_1.useState)({
        foreground: qrCodeColor,
        background: backgroundColor,
        padding: margin,
    });
    const [vCard, setVCard] = (0, react_1.useState)(undefined);
    const [url, setUrl] = (0, react_1.useState)(undefined);
    const [selectedType, setSelectedType] = (0, react_1.useState)('url');
    const [uploadedLogo, setUploadedLogo] = (0, react_1.useState)([]);
    const { uploadQRCode } = (0, useQRCodeUploader_1.useQRCodeUploader)({
        type: 'logo',
        companionUrl,
        bucketName,
        objectKey,
    });
    const isValidQRCode = (0, react_1.useMemo)(() => {
        if (selectedType === 'vcard') {
            return (0, qrcode_utils_1.validateVCard)(vCard);
        }
        if (selectedType === 'url') {
            return (0, qrcode_utils_1.validateUrl)(url);
        }
        return false;
    }, [selectedType, url, vCard]);
    const requirePremium = (0, react_1.useCallback)((isRequiredPremium = true) => new Promise((resolve) => {
        const isActionAllowed = !(isRequiredPremium && !isProUser);
        if (!isActionAllowed) {
            showProUserPaywall(true);
        }
        return resolve(isActionAllowed);
    }), [isProUser]);
    const onLogoUpload = (0, react_1.useCallback)(() => {
        uploadQRCode().then((url) => {
            const content = url;
            if (!content || content.length === 0)
                return;
            setUploadedLogo((prev) => prev.concat(content));
        });
    }, [uploadQRCode]);
    const onLogoChange = (0, react_1.useCallback)((url) => {
        setQRCodeOption((prev) => (Object.assign(Object.assign({}, prev), (url
            ? {
                image: { url, width: '25%', height: '25%' },
            }
            : { image: undefined }))));
    }, []);
    const handleGenerateQRCode = (0, react_1.useCallback)(() => {
        requirePremium(selectedType === 'vcard').then((isActionAllowed) => {
            if (!isActionAllowed)
                return;
            const value = selectedType === 'url' ? url : vCard;
            onGenerateQRCodeEvent &&
                onGenerateQRCodeEvent(selectedType, value, qrCodeOption);
        });
    }, [
        selectedType,
        url,
        vCard,
        onGenerateQRCodeEvent,
        qrCodeOption,
        requirePremium,
    ]);
    const internalWaterMarkUrl = (0, react_1.useMemo)(() => {
        if (selectedType === 'vcard' && !isProUser) {
            return waterMarkUrl;
        }
        return undefined;
    }, [isProUser, selectedType, waterMarkUrl]);
    const handleOptionsChange = (0, react_1.useCallback)((value, type) => {
        requirePremium(type === 'qrcode').then((isActionAllowed) => {
            if (!isActionAllowed)
                return;
            setQRCodeOption((prev) => (0, qrcode_utils_1.mergeCustomisation)(prev, type, value));
        });
    }, [setQRCodeOption]);
    const handleFormChange = (0, react_1.useCallback)((value, type) => {
        if (type === 'url') {
            setUrl(value);
        }
        else {
            setVCard((prev) => (0, qrcode_utils_1.mergeVCard)(prev, type, value));
        }
    }, []);
    const handleUploadLogoToggle = (0, react_1.useCallback)((value) => {
        setIsUploadLogo(value);
        if (!value) {
            setQRCodeOption((prev) => (Object.assign(Object.assign({}, prev), { image: undefined })));
        }
    }, []);
    const validateForm = (0, react_1.useCallback)((value, type) => {
        switch (type) {
            case 'url':
                return (0, qrcode_utils_1.validateUrl)(value);
            case 'phone':
                return (0, qrcode_utils_1.validatePhone)(value);
            default:
                return true;
        }
    }, []);
    const handleTabChange = (0, react_1.useCallback)((index) => {
        const value = index === 0 ? 'url' : 'vcard';
        setSelectedType(value);
    }, []);
    const subTitle = t('tool.qrcode.subtitle');
    const tabs = [
        {
            label: t('tool.qrcode.qrcode'),
            content: ((0, jsx_runtime_1.jsxs)(QRCode_style_1.StyledTabsSubtitle, { variant: variant, children: [subTitle && variant === 'headless' && ((0, jsx_runtime_1.jsx)(ui_common_components_1.Text, { size: 'Small', color: theme.Color.Neutrals.Text, children: subTitle })), (0, jsx_runtime_1.jsx)(components_2.QRCodeBody, { onChange: (value) => handleFormChange(value, 'url'), validate: (value) => validateForm(value, 'url') })] })),
        },
        {
            label: t('tool.qrcode.contact_card'),
            content: ((0, jsx_runtime_1.jsxs)(QRCode_style_1.StyledTabsSubtitle, { variant: variant, children: [subTitle && variant === 'headless' && ((0, jsx_runtime_1.jsx)(ui_common_components_1.Text, { size: 'Small', color: theme.Color.Neutrals.Text, children: subTitle })), (0, jsx_runtime_1.jsx)(components_2.ContactCardBody, { validate: validateForm, onFormControlsChangedEvent: handleFormChange })] })),
        },
    ];
    return ((0, jsx_runtime_1.jsx)(components_1.MenuShell, { visible: true, children: (0, jsx_runtime_1.jsxs)(QRCode_style_1.StyledContainer, { children: [variant === 'default' && ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(ui_common_components_1.Text, { size: 'Heading', children: t('tool.qrcode.title') }), (0, jsx_runtime_1.jsx)(ui_common_components_1.Text, { size: 'Small', color: theme.Color.Neutrals.Text, children: t('tool.qrcode.subtitle') })] })), (0, jsx_runtime_1.jsx)(QRCode_style_1.StyledTabHeader, { items: tabs, variant: variant === 'default' ? 'filled' : 'default', onTabChange: handleTabChange }), (0, jsx_runtime_1.jsx)(ui_common_components_1.Text, { size: 'Body', children: t('tool.qrcode.preview') }), (0, jsx_runtime_1.jsx)(QRCodePreview, { type: selectedType, value: selectedType === 'url' ? url : vCard, qrCodeOption: qrCodeOption, waterMarkUrl: internalWaterMarkUrl }), (0, jsx_runtime_1.jsxs)(QRCode_style_1.StyledRow, { children: [(0, jsx_runtime_1.jsxs)(QRCode_style_1.StyledTextWithIcon, { children: [(0, jsx_runtime_1.jsx)(ui_common_components_1.Text, { size: theme.Form.Input.Size, children: t('tool.qrcode.upload_logo') }), !isProUser && ((0, jsx_runtime_1.jsx)(ui_common_components_1.Icon, { name: theme.Components.QrCode.Badge.Pro, size: theme.Components.QrCode.Badge.Size }))] }), (0, jsx_runtime_1.jsx)(PremiumToggle_1.PremiumToggle, { isProUser: isProUser, checked: isUploadLogo, onChange: handleUploadLogoToggle, showProUserPaywall: showProUserPaywall })] }), isUploadLogo && uploadedLogo.length === 0 && ((0, jsx_runtime_1.jsxs)(QRCode_style_1.StyledLogoUploader, { children: [(0, jsx_runtime_1.jsxs)(ui_common_components_1.Text, { size: 'Body', children: [t('tool.qrcode.drop_files_here_or'), "\u00A0"] }), (0, jsx_runtime_1.jsx)(QRCode_style_1.StyledInlineHyperLink, { onClick: onLogoUpload, children: (0, jsx_runtime_1.jsx)(QRCode_style_1.StyledInlineText, { size: 'Body', children: t('tool.qrcode.import_file') }) })] })), isUploadLogo && uploadedLogo.length > 0 && ((0, jsx_runtime_1.jsx)(QRCodeLogo_1.QRCodeLogo, { borderColor: borderColor, onChange: handleOptionsChange, marginLevel: margin, minMargin: minMargin, maxMargin: maxMargin, logos: uploadedLogo, onLogoChange: onLogoChange, onLogoUpload: onLogoUpload })), (0, jsx_runtime_1.jsx)(ui_common_components_1.Collapse, { variant: 'ghost', isOpened: isCustomisedOpen, onChange: setIsCustomisedOpen, label: {
                        text: ((0, jsx_runtime_1.jsx)(ui_common_components_1.Text, { size: theme.Form.Input.Size, children: t('tool.qrcode.customise') })),
                        extra: ((0, jsx_runtime_1.jsx)(ui_common_components_1.Icon, { name: 'chevron---down', size: 'Medium', angle: isCustomisedOpen ? 180 : 0, color: theme.Color.Neutrals.Text })),
                    }, children: (0, jsx_runtime_1.jsx)(components_2.QRCodeCustomise, { backgroundColor: backgroundColor, qrCodeColor: qrCodeColor, marginLevel: margin, minMargin: minMargin, maxMargin: maxMargin, isProUser: isProUser, onChange: handleOptionsChange, variant: variant, showProUserPaywall: showProUserPaywall }) }), (0, jsx_runtime_1.jsx)(ui_common_components_1.Button, Object.assign({ disabled: !isValidQRCode }, (selectedType === 'vcard' && !isProUser
                    ? {
                        icon: {
                            name: theme.Components.QrCode.Badge.Pro,
                            position: 'end',
                            size: theme.Components.QrCode.Badge.Size,
                        },
                    }
                    : {}), { onClick: handleGenerateQRCode, children: t('tool.qrcode.generate_qrcode') }))] }) }));
}
exports.QRCode = QRCode;
//# sourceMappingURL=QRCode.js.map